import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Text, ActionButton, Icon } from "../custom-components";
import { IcEditPen, IcTrash, IcGroup, IcSearch, IcRefresh, IcDownload, IcDocumentViewer, IcSort, IcFilter, IcInfo } from "../custom-components/Icon";
import { BsSearch, BsThreeDotsVertical } from "react-icons/bs";
import { listRoles, deleteRole } from "../store/actions/CommonAction";
import "../styles/toast.css";
import { Slide, ToastContainer, toast } from "react-toastify";
import CustomToast from "./CustomToastContainer";
import "../styles/businessGroups.css";
import "../styles/pageConfiguration.css";

const Roles = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await dispatch(listRoles());
        setRoles(response || []);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
        setRoles([]); // Ensure roles is an array on error
      }
    };
    fetchRoles();
  }, [dispatch]);

  const handleAddRoleClick = () => {
    navigate("/addRole");
  };

  const handleUpdate = (roleId) => {
    navigate(`/updateRole/${roleId}`);
  };

  const handleDelete = async (roleId) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        const response = await dispatch(deleteRole(roleId));
        if (response && response.status === 200) {
          setRoles(roles.filter(role => role.roleId !== roleId));
          toast.success(
            (props) => (
              <CustomToast
                {...props}
                type="success"
                message={"Role deleted successfully!"}
              />
            ),
            { icon: false }
          );
        } else {
          toast.error(
            (props) => (
              <CustomToast
                {...props}
                type="error"
                message={"Failed to delete role. Please check the console."}
              />
            ),
            { icon: false }
          );
        }
      } catch (error) {
        toast.error(
          (props) => (
            <CustomToast
              {...props}
              type="error"
              message={"An error occurred while deleting the role."}
            />
          ),
          { icon: false }
        );
        console.error("Delete role error:", error);
      }
    }
  };

  const handleView = (roleId) => {
    navigate(`/addRole?mode=view&id=${roleId}`);
  };

  const sortedRoles = React.useMemo(() => {
    if (!sortColumn) return roles;

    return [...roles].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'permissions') {
        aValue = a.permissions ? a.permissions.map(p => p.componentName).join(", ") : "N/A";
        bValue = b.permissions ? b.permissions.map(p => p.componentName).join(", ") : "N/A";
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [roles, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderSortIcon = () => {
    return <Icon ic={<IcSort />} size="small" color="black" />;
  };

  return (
    <>
      <div className="configurePage">
        <div className="bs-page">
          <div className="bs-header-container">
            <div className="bs-header-left">
              <div className="bs-header-icon-container">
                <Icon ic={<IcGroup height={24} width={24} />} color="primary_60" />
              </div>
              <div className="bs-header-text inline-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text appearance="heading-s" color="primary-grey-100">
                  Roles & Permissions
                </Text>
                <div className="custom-header-tooltip-container" style={{ display: 'flex' }}>
                  <div className="custom-header-tooltip-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </div>
                  <div className="custom-header-tooltip-text" style={{ width: '280px', fontWeight: 'normal' }}>
                    Manage system roles and define permissions for users across the consent management platform.
                  </div>
                </div>
              </div>
            </div>
            <div className="bs-header-right" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={handleAddRoleClick}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)', whiteSpace: 'nowrap' }}
              >
                + Create Role
              </button>
            </div>
          </div>

          <div className="summary-cards-container">
            <div className="summary-card">
              <div className="summary-icon blue">
                <Icon ic={<IcGroup />} />
              </div>
              <div className="summary-info">
                <span className="summary-value">{roles.length || 0}</span>
                <span className="summary-label">Total Roles</span>
                <span className="summary-subtext">All system roles</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon green">
                <Icon ic={<IcGroup />} />
              </div>
              <div className="summary-info">
                <span className="summary-value">{roles.length || 0}</span>
                <span className="summary-label">Active Roles</span>
                <span className="summary-subtext">Currently in use</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon yellow">
                <Icon ic={<IcGroup />} />
              </div>
              <div className="summary-info">
                <span className="summary-value">0</span>
                <span className="summary-label">Inactive Roles</span>
                <span className="summary-subtext">Not in use</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon purple">
                <Icon ic={<IcGroup />} />
              </div>
              <div className="summary-info">
                <span className="summary-value">{roles.length || 0}</span>
                <span className="summary-label">Users Assigned</span>
                <span className="summary-subtext">Across all roles</span>
              </div>
            </div>
          </div>

          <div className="bs-content">
            <div className="business-table-card" style={{ minHeight: '350px', overflow: 'visible' }}>
              <div className="business-table-container" style={{ overflow: 'visible' }}>
                <table className="business-table">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th onClick={() => handleSort('role')} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Role Name</span>
                          {renderSortIcon()}
                        </div>
                      </th>
                      <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Description</span>
                          {renderSortIcon()}
                        </div>
                      </th>
                      <th onClick={() => handleSort('permissions')} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Access Level</span>
                          {renderSortIcon()}
                        </div>
                      </th>
                      <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Created On</span>
                          {renderSortIcon()}
                        </div>
                      </th>
                      <th>
                        <span>Status</span>
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRoles && sortedRoles.length > 0 ? (
                      sortedRoles.map((role, index) => {
                        let accessBadgeClass = "default";
                        let accessLabel = role.role || "N/A";
                        
                        if (accessLabel.toLowerCase().includes("admin")) {
                          accessBadgeClass = "admin";
                          accessLabel = "Administrator";
                        } else if (accessLabel.toLowerCase().includes("dpo")) {
                          accessBadgeClass = "compliance";
                          accessLabel = "Compliance";
                        } else if (accessLabel.toLowerCase().includes("data_protection")) {
                           accessBadgeClass = "compliance";
                           accessLabel = "Compliance";
                        }
                        
                        return (
                          <tr key={role.roleId}>
                            <td>{index + 1}</td>
                            <td style={{ fontWeight: 600 }}>{role.role || "N/A"}</td>
                            <td>{role.description || "N/A"}</td>
                            <td>
                               <span className={`access-badge ${accessBadgeClass}`}>{accessLabel}</span>
                            </td>
                            <td>{role.createdAt ? formatDate(role.createdAt) : "N/A"}</td>
                            <td>
                              <span className="status-badge active">
                                <span className="status-dot"></span> Active
                              </span>
                            </td>
                            <td>
                              <div className="action-icons" style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                                <button
                                  className="icon-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(openDropdownId === role.roleId ? null : role.roleId);
                                  }}
                                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px' }}
                                >
                                  <BsThreeDotsVertical size={18} color="#64748B" />
                                </button>

                                {openDropdownId === role.roleId && (
                                  <div className="action-dropdown-menu" style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '100%',
                                    background: 'white',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                                    zIndex: 99,
                                    minWidth: '150px',
                                    padding: '4px 0',
                                    textAlign: 'left'
                                  }}>
                                    <div 
                                      onClick={(e) => { e.stopPropagation(); handleUpdate(role.roleId); setOpenDropdownId(null); }}
                                      style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#0F172A' }}
                                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      <IcEditPen height={16} width={16} />
                                      Edit Role
                                    </div>
                                    <div 
                                      onClick={(e) => { e.stopPropagation(); handleView(role.roleId); setOpenDropdownId(null); }}
                                      style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#0F172A' }}
                                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      <IcDocumentViewer height={16} width={16} color="#0F172A" />
                                      View Role
                                    </div>
                                    {role.role && role.role.toLowerCase() !== 'admin' && (
                                      <div 
                                        onClick={(e) => { e.stopPropagation(); handleDelete(role.roleId); setOpenDropdownId(null); }}
                                        style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#EF4444' }}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                      >
                                        <IcTrash height={16} width={16} color="#EF4444" />
                                        Delete Role
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>No roles found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="business-pagination">
                <Text appearance="body-xs" color="primary-grey-60">
                  Showing 1 to {sortedRoles?.length || 0} of {sortedRoles?.length || 0} roles
                </Text>
                <div className="pagination-controls">
                  <button className="page-btn disabled">&lt;</button>
                  <button className="page-btn active">1</button>
                  <button className="page-btn disabled">&gt;</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-left"
        autoClose={3000}
        hideProgressBar
        closeOnClick
        pauseOnHover
        draggable
        closeButton={false}
        toastClassName={() => "toast-wrapper"}
        transition={Slide}
      />
    </>
  );
};

export default Roles;
